import { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import SectionHeading from './SectionHeading';

const types = [
  'Oficina',
  'Edificio',
  'Hotel',
  'Restaurante',
  'Cocina industrial',
  'Escuela',
  'Centro comercial',
  'Institución gubernamental',
  'Fraccionamiento',
  'Área pública',
  'Otro',
];

const freqs = [
  'Servicio único',
  'Diario',
  'Semanal',
  'Quincenal',
  'Mensual',
  'Personalizado',
];

const initial = {
  nombre: '',
  empresa: '',
  telefono: '',
  correo: '',
  tipo_instalacion: 'Oficina',
  tamano_espacio: '',
  frecuencia: 'Diario',
  mensaje: '',
};

// ✅ Email actualizado
const EMAIL_TO = 'aydnieto@gmail.com';

const QuoteForm = () => {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!form.nombre || !form.correo || !form.telefono) {
      setError('Por favor completa todos los campos requeridos.');
      return;
    }

    setSending(true);
    setError('');

    try {
      const subject = encodeURIComponent(
        `Solicitud de cotización - ${form.empresa || 'Sin empresa'}`
      );

      const body = encodeURIComponent(
        `Nueva solicitud de cotización:

Nombre: ${form.nombre}
Empresa: ${form.empresa || 'No especificada'}
Teléfono: ${form.telefono}
Correo: ${form.correo}

Tipo de instalación: ${form.tipo_instalacion}
Tamaño del espacio: ${form.tamano_espacio || 'No especificado'}
Frecuencia requerida: ${form.frecuencia}

Mensaje:
${form.mensaje || 'Sin mensaje adicional'}

---
Este mensaje fue enviado desde el formulario de contacto de Clean Work.`
      );

      window.location.href = `mailto:${EMAIL_TO}?subject=${subject}&body=${body}`;

      const submissions = JSON.parse(localStorage.getItem('cleanwork_quotes') || '[]');
      submissions.push({
        ...form,
        fecha: new Date().toISOString(),
        estado: 'Nueva',
        id: Date.now(),
      });
      localStorage.setItem('cleanwork_quotes', JSON.stringify(submissions));

      setForm(initial);
      setDone(true);
      setTimeout(() => setDone(false), 5000);
    } catch {
      setError('No pudimos abrir tu cliente de correo. Intenta nuevamente.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="cotizacion" className="bg-white py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 lg:grid-cols-[.75fr_1.25fr] lg:px-8">
        <div>
          <SectionHeading
            eyebrow="Hablemos de tu empresa"
            title="SOLICITA UNA COTIZACIÓN"
            text="Cuéntanos sobre tus instalaciones. Nuestro equipo podrá ayudarte a desarrollar una solución de limpieza adaptada a tus necesidades."
          />
          <div className="mt-9 border-l-4 border-emerald-500 bg-slate-50 p-6 text-[#0A2540]">
            <b>Una propuesta hecha para ti</b>
            <p className="mt-2 text-sm leading-6 text-slate-600 text-justify">
              Evaluamos personal, rutinas, materiales, frecuencia y presupuesto.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-5 bg-slate-50 p-6 sm:grid-cols-2 lg:p-9">
          {[
            ['nombre', 'Nombre completo'],
            ['empresa', 'Empresa'],
            ['telefono', 'Teléfono'],
            ['correo', 'Correo electrónico'],
            ['tamano_espacio', 'Tamaño aproximado del espacio'],
          ].map(([name, label]) => (
            <label key={name} className={name === 'tamano_espacio' ? 'sm:col-span-2' : ''}>
              <span className="mb-2 block text-sm font-bold text-[#0A2540]">
                {label} <i className="text-emerald-600">*</i>
              </span>
              <input
                required
                name={name}
                value={form[name]}
                onChange={handleChange}
                type={name === 'correo' ? 'email' : 'text'}
                className="h-12 w-full border border-slate-300 bg-white px-4"
              />
            </label>
          ))}

          <label>
            <span className="mb-2 block text-sm font-bold text-[#0A2540]">
              Tipo de instalación
            </span>
            <select
              name="tipo_instalacion"
              value={form.tipo_instalacion}
              onChange={handleChange}
              className="h-12 w-full border border-slate-300 bg-white px-4"
            >
              {types.map((type) => (
                <option key={type}>{type}</option>
              ))}
            </select>
          </label>

          <label>
            <span className="mb-2 block text-sm font-bold text-[#0A2540]">
              Frecuencia requerida
            </span>
            <select
              name="frecuencia"
              value={form.frecuencia}
              onChange={handleChange}
              className="h-12 w-full border border-slate-300 bg-white px-4"
            >
              {freqs.map((freq) => (
                <option key={freq}>{freq}</option>
              ))}
            </select>
          </label>

          <label className="sm:col-span-2">
            <span className="mb-2 block text-sm font-bold text-[#0A2540]">
              Mensaje
            </span>
            <textarea
              name="mensaje"
              value={form.mensaje}
              onChange={handleChange}
              rows="4"
              className="w-full border border-slate-300 bg-white p-4"
            />
          </label>

          {done && (
            <p className="flex items-center gap-2 text-sm font-bold text-emerald-700 sm:col-span-2">
              <CheckCircle2 size={18} />
              Solicitud enviada correctamente.
            </p>
          )}
          {error && (
            <p className="text-sm font-bold text-red-700 sm:col-span-2">{error}</p>
          )}

          <button
            disabled={sending}
            className="px-7 py-4 font-bold text-white hover:bg-emerald-700 disabled:opacity-60 sm:col-span-2 bg-[#00a7e2] transition-colors"
          >
            {sending ? 'ABRIENDO CORREO...' : 'SOLICITAR COTIZACIÓN'}
          </button>
        </form>
      </div>
    </section>
  );
};

export default QuoteForm;